import React from 'react';
import { strings } from '../../../resources/strings';
import { AssemblerManagementOption } from '../../../enums/AssemblerManagementOption';
import ServicesContext from '../../../contexts/ServicesContext';
import { OrderItem } from '../../../models/OrderItem';
import { ActionsBox } from '../../../common-components/Layout/ActionsBox/ActionsBox';
import { Table } from '../../../common-components/Table/Table';
import { TableRow } from '../../../common-components/TableRow/TableRow';
import { TableHead } from '../../../common-components/TableHead/TableHead';
import { TableData } from '../../../common-components/TableData/TableData';
import { Field, FieldType } from '../../../common-components/Field/Field';
import { Container, ContainerHeader, HeaderTitle, ContainerBody, ContainerFooter, HeaderSubtitle } from './styles';

export interface IAssemblerOperationFormProps {
  assemblerManagementOption?: AssemblerManagementOption|null;
  items?: OrderItem[];
  cancel?: () => void;
  apply?: (informedQuantities: IInformedQuantity[]) => void;
}

export interface IInformedQuantity {
  orderItemId: number;
  quantity: number;
}

const formActions = {
  Cancel: 'cancel',
  Apply: 'apply'
}

const defaultTextInformationForGeneralSelectedOption = 'Vale lembrar que a nova quantidade será adicionada a quantidade já existente.';

type AssemblerManagementOptionMappingType = {
  supplementaryText: string;
  quantityColumnName: string;
  orderItemPropertyName: string;
}

const assemblerManagementOptionMapping: any = {
  [AssemblerManagementOption.InformReceipt]: {
    supplementaryText: 'Vale lembrar que a nova quantidade será adicionada a quantidade já recebida.',
    quantityColumnName: 'Quantidade já Recebida',
    orderItemPropertyName: 'assemblerReceivedConfirmedQuantity'
  },
  [AssemblerManagementOption.InformAssemblyStart]: {
    supplementaryText: 'Vale lembrar que a nova quantidade será adicionada a quantidade já em montagem.',
    quantityColumnName: 'Quantidade já Recebida',
    orderItemPropertyName: 'assemblyStartConfirmedQuantity'
  },
  [AssemblerManagementOption.InformCompletedAssembly]: {
    supplementaryText: 'Vale lembrar que a nova quantidade será adicionada a quantidade já montada.',
    quantityColumnName: 'Quantidade já Montada',
    orderItemPropertyName: 'assemblyEndConfirmedQuantity'
  },
  [AssemblerManagementOption.InformSendToStore]: {
    supplementaryText: 'Vale lembrar que a nova quantidade será adicionada a quantidade já enviada.',
    quantityColumnName: 'Quantidade já Enviada',
    orderItemPropertyName: 'sentToStoreConfirmedQuantity'
  },
}

const assemblerManagementOptionMapper = (assemblerManagementOption: AssemblerManagementOption): AssemblerManagementOptionMappingType =>
  assemblerManagementOptionMapping[assemblerManagementOption];

export const AssemblerOperationForm: React.FC<IAssemblerOperationFormProps> = ({
  assemblerManagementOption,
  items,
  cancel,
  apply
}) => {

  const [informedQuantities, setInformedQuantities] = React.useState<IInformedQuantity[]>([]);

  const {
    alertService
  } = React.useContext(ServicesContext);

  if (!assemblerManagementOption) return null;

  return (
    <Container>
      <ContainerHeader>
        <HeaderTitle>Informe a nova quantidade e acione o botão Aplicar.</HeaderTitle>
        <HeaderSubtitle>
          {assemblerManagementOptionMapper(assemblerManagementOption).supplementaryText || defaultTextInformationForGeneralSelectedOption}
        </HeaderSubtitle>
      </ContainerHeader>
      <ContainerBody>
        <Table>
          <thead>
            <TableRow>
              <TableHead width='130px'>{strings.Sku}</TableHead>
              <TableHead>{strings.Name}</TableHead>
              <TableHead width='150px' textAlign='center'>{strings.Quantity}</TableHead>
              <TableHead width='150px' textAlign='center'>{assemblerManagementOptionMapper(assemblerManagementOption).quantityColumnName}</TableHead>
              <TableHead width='150px' textAlign='center'>Nova Quantidade</TableHead>
            </TableRow>
          </thead>
          <tbody>
            {items?.filter(item => item.active)?.map(orderItem => {
              const itemIsNotActive = orderItem.canceledAt || orderItem.returnedAt || !orderItem.active;
              const textColor = itemIsNotActive ? 'red' : null;

              const operationCount = +((orderItem as any)[assemblerManagementOptionMapper(assemblerManagementOption).orderItemPropertyName] || 0);
              const remainingQuantity = orderItem.quantity;
              const remainingQuantities: number[] = remainingQuantity > 0 ? Array.from({ length: remainingQuantity+1 }, (_, index) => index) : [];
              const newQuantityField = remainingQuantity > 0 ? (
                <Field
                  type={FieldType.Select}
                  selectProps={{
                    options: remainingQuantities.map(qty => ({ quantity: qty, text: `${qty}` })),
                    displayField: 'text',
                    keyField: 'quantity'
                  }}
                  value={informedQuantities.find(item => item.orderItemId === orderItem.id)?.quantity || 0}
                  marginBottom='0px'
                  marginTop='0px'
                  change={(value) => applyInformedQuantity(orderItem.id, +value)}
                />
              ) : null;

              return (
                <TableRow key={`item-${orderItem.id}`}>
                  <TableData textColor={textColor} data-id={orderItem.id}>{orderItem.sku}</TableData>
                  <TableData textColor={textColor}>{orderItem.title}{orderItem.variantTitle ? ` - ${orderItem.variantTitle}` : ''}</TableData>
                  <TableData width='95px' textAlign='center' textColor={textColor}>{orderItem.quantity}</TableData>
                  <TableData textColor={textColor} textAlign='center'>
                    {operationCount}
                  </TableData>
                  <TableData textColor={textColor} textAlign='center'>
                    {newQuantityField}
                  </TableData>
                </TableRow>
              );
            })}
          </tbody>
        </Table>
      </ContainerBody>
      <ContainerFooter>
        <ActionsBox
          actions={[
            { key: formActions.Cancel, text: strings.Cancel },
            { key: formActions.Apply, text: strings.Apply }
          ]}
          click={actionsBoxClick}
        />
      </ContainerFooter>
    </Container>
  );

  function applyInformedQuantity(orderItemId: number, quantity: number) {
    const newInformedQuantities = informedQuantities.filter(item => item.orderItemId !== orderItemId);
    if (quantity > 0) {
      newInformedQuantities.push({
        orderItemId: orderItemId,
        quantity: quantity
      });
    }
    setInformedQuantities(newInformedQuantities);
  }

  function actionsBoxClick(key: string) {
    if (key === formActions.Cancel && cancel) {
      cancel();
      return;
    }

    if (key === formActions.Apply && apply) {
      if (!informedQuantities || informedQuantities.length === 0) {
        alertService.warning(`Nenhuma nova quantidade informada!`);
        return;
      }
      apply(informedQuantities);
      return;
    }
  }

}